import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { ChannelPublic, ChannelUpdate } from '@streamr/shared';
import { api } from '../api/client';
import { routes } from '../api/routes';
import { Button, Field, inputClass } from '../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { errorMessage } from '../api/errors';

/** Owner-only edit of the channel profile. RBAC is the API's job; this only offers the form. */
export const SettingsPage = (): JSX.Element => {
  const { user, refreshUser } = useAuth();
  const [displayName, setDisplayName] = useState(user?.channel.displayName ?? '');
  const [handle, setHandle] = useState(user?.channel.handle ?? '');
  const [avatarUrl, setAvatarUrl] = useState(user?.channel.avatarUrl ?? '');
  const [bannerUrl, setBannerUrl] = useState('');
  const [bio, setBio] = useState('');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (update: ChannelUpdate) => api.patch<ChannelPublic>(routes.channels.me(), update),
    onSuccess: () => {
      setSaved(true);
      void refreshUser();
    },
  });

  if (user === null) return <p className="text-sm text-ink-300">Sign in to edit your channel.</p>;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setSaved(false);
    // Empty string is not "no change" -- it is how the form says "clear this
    // field", and the DTO models a cleared field as null.
    save.mutate({
      displayName,
      handle,
      avatarUrl: avatarUrl.trim() === '' ? null : avatarUrl.trim(),
      bannerUrl: bannerUrl.trim() === '' ? null : bannerUrl.trim(),
      bio: bio.trim() === '' ? null : bio.trim(),
    });
  };

  return (
    <form onSubmit={submit} className="mx-auto flex max-w-xl flex-col gap-5">
      <h1 className="text-xl font-semibold">Channel settings</h1>

      <Field label="Display name" htmlFor="displayName">
        <input
          id="displayName"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          maxLength={60}
          className={inputClass}
        />
      </Field>

      <Field label="Handle" htmlFor="handle" hint="Changing this changes your channel's public address.">
        <input
          id="handle"
          value={handle}
          onChange={(event) => setHandle(event.target.value.toLowerCase())}
          className={inputClass}
        />
      </Field>

      <Field label="Avatar URL" htmlFor="avatarUrl">
        <input id="avatarUrl" value={avatarUrl} onChange={(event) => setAvatarUrl(event.target.value)} className={inputClass} />
      </Field>

      <Field label="Banner URL" htmlFor="bannerUrl">
        <input id="bannerUrl" value={bannerUrl} onChange={(event) => setBannerUrl(event.target.value)} className={inputClass} />
      </Field>

      <Field label="Bio" htmlFor="bio">
        <textarea id="bio" value={bio} onChange={(event) => setBio(event.target.value)} rows={4} maxLength={1000} className={inputClass} />
      </Field>

      {save.isError && <p className="text-sm text-red-300">{errorMessage(save.error)}</p>}
      {saved && <p className="text-sm text-emerald-300">Saved.</p>}

      <div>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
};
